import { useNavigate } from 'react-router-dom';
import { Row } from '@tanstack/react-table';
import classNames from 'classnames';

import { ButtonSize, ButtonType } from '@xemplo/button';
import { DateTimeFormat, DateTimeHelper } from '@xemplo/date-time';
import { PayrollDynamicRoutes } from '@xemplo/gp-routes';
import { PayrunListItem } from '@xemplo/gp-types';
import {
  getDateDiffFromMilestone,
  MilestoneBadgeCell,
  MilestoneText,
} from '@xemplo/payrun-base-table';

import { useAmendmentModal } from './amendment-modal';
import * as S from './payrun-list-widget.styles';

const dateTimeHelper = DateTimeHelper.with({
  format: DateTimeFormat.Default,
  fallback: '-',
});

export const PayrunWidgetCell = (props: Row<PayrunListItem>) => {
  const dateDiff = getDateDiffFromMilestone(props.original);
  const navigate = useNavigate();
  const handleViewPayrun = () =>
    navigate(`/${PayrollDynamicRoutes.PayrunById(props.original.id)}`);
  const { showAmendmentModal } = useAmendmentModal({
    payrunId: props.original.id,
  });

  return (
    <S.Container>
      <S.DetailsContainer>
        <Icon />
        <S.NameCellContainer>
          <S.Name>{props.original.name}</S.Name>
          <S.DueDateContainer>
            <MilestoneText dateDiff={dateDiff} />
            {' · Pay date: '}
            {dateTimeHelper.formatString(props.original.payDate)}
            {' · Amendments: '}
            <S.AmendmentCount
              onClick={showAmendmentModal}
              className={classNames({
                'has-amendments': props.original.amendmentCount > 0,
              })}
            >
              {props.original.amendmentCount}
            </S.AmendmentCount>
          </S.DueDateContainer>
        </S.NameCellContainer>
      </S.DetailsContainer>
      <MilestoneBadgeCell {...props} />
      <S.ViewButton
        type={ButtonType.Primary}
        size={ButtonSize.Small}
        onClick={handleViewPayrun}
        ariaLabel="View payrun details"
      >
        View
      </S.ViewButton>
    </S.Container>
  );
};

const Icon = () => {
  return (
    <S.IconContainer>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="23"
        viewBox="0 0 24 23"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.06469 5.12464C3.53233 5.61897 3.16623 6.42865 3.16623 7.72222V12.4908C3.16623 13.4262 3.35793 14.1056 3.65767 14.5914C3.9522 15.0686 4.37953 15.4058 4.94336 15.6169C5.11517 15.6793 5.29033 15.7294 5.46375 15.7577C5.48058 15.7602 5.49738 15.7633 5.51413 15.7671C5.65338 15.7984 5.79973 15.8224 5.95283 15.8394C6.14061 15.8602 6.33852 15.8704 6.54586 15.8704H14.6848C15.8005 15.8704 16.6242 15.5913 17.1659 15.0883C17.6983 14.594 18.0644 13.7843 18.0644 12.4908V7.72222C18.0644 7.34173 18.0245 7.00617 17.965 6.66456C17.7959 5.81891 17.4138 5.26553 16.8999 4.91047C16.3695 4.54391 15.6307 4.34262 14.6848 4.34262H6.54586C5.43008 4.34262 4.60637 4.62166 4.06469 5.12464ZM19.2236 5.9673C18.9496 5.01858 18.4282 4.27831 17.6895 3.76783C16.8565 3.19226 15.8129 2.95374 14.6848 2.95374H6.54586C5.21718 2.95374 4.00387 3.28579 3.11962 4.10687C2.22606 4.9366 1.77734 6.16393 1.77734 7.72222V12.4908C1.77734 13.6109 2.00694 14.5611 2.47572 15.3207C2.94922 16.088 3.63671 16.6116 4.46039 16.9191L4.46038 16.9192L4.46595 16.9212C4.56763 16.9582 4.67572 16.994 4.78913 17.0265C5.17479 18.3544 6.031 19.2495 7.23861 19.6968C7.87388 19.9373 8.58124 20.0371 9.32465 20.0371H17.4635C18.7922 20.0371 20.0055 19.7051 20.8898 18.884C21.7833 18.0543 22.2321 16.8269 22.2321 15.2687V10.5001C22.2321 9.21455 21.9225 8.1539 21.3025 7.35114C20.7704 6.66211 20.0498 6.21508 19.2236 5.9673ZM6.3734 17.2574C6.67926 17.8173 7.13905 18.1792 7.7229 18.3951L7.72836 18.3971L7.72836 18.3971C8.16643 18.5633 8.69879 18.6482 9.32465 18.6482H17.4635C18.5793 18.6482 19.403 18.3692 19.9447 17.8662C20.4771 17.3719 20.8432 16.5622 20.8432 15.2687V10.5001C20.8432 9.43383 20.588 8.69819 20.2033 8.20004C20.0033 7.94105 19.7534 7.72702 19.4527 7.55913C19.4537 7.61303 19.4543 7.66742 19.4543 7.72233V12.4909C19.4543 14.0492 19.0056 15.2765 18.112 16.1062C17.2278 16.9273 16.0144 17.2594 14.6858 17.2594H6.54687C6.48894 17.2594 6.43111 17.2587 6.3734 17.2574ZM10.6092 8.361C9.64275 8.361 8.85923 9.14451 8.85923 10.111C8.85923 11.0775 9.64275 11.861 10.6092 11.861C11.5757 11.861 12.3593 11.0775 12.3593 10.111C12.3593 9.14451 11.5757 8.361 10.6092 8.361ZM7.47034 10.111C7.47034 8.37745 8.87568 6.97211 10.6092 6.97211C12.3428 6.97211 13.7481 8.37745 13.7481 10.111C13.7481 11.8446 12.3428 13.2499 10.6092 13.2499C8.87568 13.2499 7.47034 11.8446 7.47034 10.111ZM5.31444 7.37963C5.69798 7.37963 6.00889 7.69055 6.00889 8.07408V12.1482C6.00889 12.5317 5.69798 12.8426 5.31444 12.8426C4.93091 12.8426 4.62 12.5317 4.62 12.1482V8.07408C4.62 7.69055 4.93091 7.37963 5.31444 7.37963ZM15.9086 7.37991C16.2921 7.37991 16.603 7.69083 16.603 8.07436V12.1485C16.603 12.532 16.2921 12.8429 15.9086 12.8429C15.525 12.8429 15.2141 12.532 15.2141 12.1485V8.07436C15.2141 7.69083 15.525 7.37991 15.9086 7.37991Z"
          fill="url(#paint0_linear_1290_32090)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_1290_32090"
            x1="1.77734"
            y1="20.0371"
            x2="18.5874"
            y2="-0.0902471"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#3991FF" />
            <stop offset="0.501502" stopColor="#A48AFB" />
            <stop offset="1" stopColor="#FA7066" />
          </linearGradient>
        </defs>
      </svg>
    </S.IconContainer>
  );
};
